// Self-contained Mapbox map for search results. Isolated on purpose: if the map
// can't load (no token, bad coords) it renders a graceful fallback and never
// touches the list view. v1 plots one pin per property + the search anchor, with
// a popup (name · price · distance) for the visual fact-check of proximity results.
import React from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const ATLANTA: [number, number] = [-84.388, 33.749];

export interface MapPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  price?: number | null;
  distanceMiles?: number | null;
}
export interface MapAnchor {
  lat: number;
  lng: number;
  label?: string;
}
interface ResultsMapProps {
  points: MapPoint[];
  anchor?: MapAnchor | null;
  height?: number;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string)
  );
}

const ResultsMap: React.FC<ResultsMapProps> = ({ points, anchor, height = 520 }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<mapboxgl.Map | null>(null);
  const markersRef = React.useRef<mapboxgl.Marker[]>([]);

  React.useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      // Light street style — approachable, with green parks. Swap to dark-v11
      // or a custom Mapbox Studio style URL here to restyle in one line.
      style: "mapbox://styles/mapbox/streets-v12",
      center: anchor ? [anchor.lng, anchor.lat] : ATLANTA,
      zoom: 10,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    // Keep parks/green spaces (Piedmont Park etc.) but drop the commercial
    // landmark POIs (museum/attraction icons) by filtering the POI layer to the
    // "park_like" class. ("Pick and choose" more categories / add filters here.)
    map.on("load", () => {
      try {
        if (map.getLayer("poi-label")) {
          map.setFilter("poi-label", ["==", ["get", "class"], "park_like"]);
        }
      } catch {
        /* class values vary by style version — best effort */
      }
    });
    mapRef.current = map;
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const draw = () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      const bounds = new mapboxgl.LngLatBounds();
      let any = false;

      if (anchor && Number.isFinite(anchor.lat) && Number.isFinite(anchor.lng)) {
        const el = document.createElement("div");
        el.style.cssText =
          "width:18px;height:18px;border-radius:50%;background:#7c4dff;border:3px solid #fff;box-shadow:0 0 0 3px rgba(124,77,255,.35);";
        const m = new mapboxgl.Marker(el)
          .setLngLat([anchor.lng, anchor.lat])
          .setPopup(new mapboxgl.Popup({ offset: 16 }).setText(anchor.label || "Search location"))
          .addTo(map);
        markersRef.current.push(m);
        bounds.extend([anchor.lng, anchor.lat]);
        any = true;
      }

      points.forEach((p) => {
        if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
        const el = document.createElement("div");
        el.style.cssText =
          "width:15px;height:15px;border-radius:50%;background:#005dff;border:2px solid #fff;cursor:pointer;box-shadow:0 0 0 1px rgba(0,0,0,.2),0 1px 4px rgba(0,0,0,.4);";
        const price = p.price ? `$${Math.round(p.price).toLocaleString()}` : "";
        const dist =
          typeof p.distanceMiles === "number" ? ` · ${p.distanceMiles.toFixed(1)} mi` : "";
        const m = new mapboxgl.Marker(el)
          .setLngLat([p.lng, p.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 14 }).setHTML(
              `<div style="font:13px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;color:#1a1a2e;padding:1px 2px"><strong style="color:#0a0a23;font-size:14px">${escapeHtml(
                p.name
              )}</strong><br/><span style="color:#475569">${price}${dist}</span></div>`
            )
          )
          .addTo(map);
        markersRef.current.push(m);
        bounds.extend([p.lng, p.lat]);
        any = true;
      });

      if (any && !bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 64, maxZoom: 14, duration: 400 });
      }
    };
    if (map.isStyleLoaded()) draw();
    else map.once("load", draw);
  }, [points, anchor]);

  if (!MAPBOX_TOKEN) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#9aa0a6", fontSize: 14 }}>
        Map unavailable — no map token configured.
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height, borderRadius: 12, overflow: "hidden" }}
      aria-label="Map of search results"
    />
  );
};

export default ResultsMap;
